import { prefixClass } from 'lib/utils';
import { format } from 'date-fns';
import type { AskRequestType, UserLocation } from '@hone-automation/common';
import type { WorkState } from '../../../../components/RequestCard';
import { IconCancel, IconFilter } from 'components/Icons';
import IconButton from '../../../../../../Common/IconButton';
import { workStateOptions, dueDateOptions, requestTypeOptions } from './filterOptions';
import './FilterModalContent.scss';

/** Assignee filter option */
export interface AssigneeFilterOption {
  userId: string;
  name: string;
}

/** Due date filter presets */
export type DueDatePreset = 'overdue' | 'today' | 'this-week' | 'this-month' | 'no-due-date' | null;

/** Date range for filtering */
export interface DateRange {
  from: Date | null;
  to: Date | null;
}

/** Request type filter - grouped for UI simplicity */
export type RequestTypeFilter = 'email' | 'transaction' | 'flagged-cell' | 'custom-request';

/** Map filter values to actual AskRequestType values */
export const requestTypeMapping: Record<RequestTypeFilter, AskRequestType[]> = {
  email: ['Email'],
  transaction: ['Ask Bookkeeper', 'Ask Customer'],
  'flagged-cell': ['Flagged Cell'],
  'custom-request': ['Customer Request'],
};

export interface FilterModalContentProps {
  // Status (WorkState) filter
  workStateFilter: Set<WorkState>;
  onWorkStateFilterChange: (states: Set<WorkState>) => void;
  workStateCounts: Record<WorkState, number>;

  // Due date filter
  dueDateFilter: DueDatePreset;
  onDueDateFilterChange: (preset: DueDatePreset) => void;

  // Date range filter
  dateRangeFilter: DateRange;
  onDateRangeFilterChange: (range: DateRange) => void;

  // Request type filter
  requestTypeFilter: Set<RequestTypeFilter>;
  onRequestTypeFilterChange: (types: Set<RequestTypeFilter>) => void;
  requestTypeCounts?: Record<RequestTypeFilter, number>;

  // Assignee filter
  assigneeFilter: string | null;
  onAssigneeFilterChange: (userId: string | null) => void;
  assigneeOptions: AssigneeFilterOption[];

  // Actions
  onClearAll: () => void;
  onClose: () => void;
}

/**
 * FilterModalContent - Two-column filter modal content
 * Left: Due Date, Date Range, Request Type
 * Right: Status (WorkState)
 */
export const FilterModalContent = ({
  workStateFilter,
  onWorkStateFilterChange,
  workStateCounts,
  dueDateFilter,
  onDueDateFilterChange,
  dateRangeFilter,
  onDateRangeFilterChange,
  requestTypeFilter,
  onRequestTypeFilterChange,
  requestTypeCounts,
  assigneeFilter,
  onAssigneeFilterChange,
  assigneeOptions,
  onClearAll,
  onClose,
}: FilterModalContentProps) => {
  const prefix = prefixClass('ksq2-filter-modal');

  // Toggle a work state in the filter set
  const toggleWorkState = (state: WorkState) => {
    const newSet = new Set(workStateFilter);
    if (newSet.has(state)) {
      newSet.delete(state);
    } else {
      newSet.add(state);
    }
    onWorkStateFilterChange(newSet);
  };

  // Toggle a request type in the filter set
  const toggleRequestType = (type: RequestTypeFilter) => {
    const newSet = new Set(requestTypeFilter);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    onRequestTypeFilterChange(newSet);
  };

  // Handle date range changes
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const fromDate = value ? new Date(value + 'T00:00:00') : null;
    onDateRangeFilterChange({ ...dateRangeFilter, from: fromDate });
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const toDate = value ? new Date(value + 'T23:59:59') : null;
    onDateRangeFilterChange({ ...dateRangeFilter, to: toDate });
  };

  // Filter out work state options with zero count
  const availableWorkStateOptions = workStateOptions.filter(option => workStateCounts[option.value] > 0);

  // Check if any filter is active
  const hasActiveFilters =
    workStateFilter.size > 0 ||
    dueDateFilter !== null ||
    dateRangeFilter.from !== null ||
    dateRangeFilter.to !== null ||
    requestTypeFilter.size > 0 ||
    assigneeFilter !== null;

  // Format date for input value
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  return (
    <div className={prefix()}>
      {/* Header */}
      <div className={prefix('__header')}>
        <span className={prefix('__info')}>
          <span className={prefix('__icon')}>
            <IconFilter />
          </span>
          <span className={prefix('__title')}>Filters</span>
        </span>
        <IconButton icon={<IconCancel />} size="small" rounded type="switch" selected={false} onClick={onClose} />
      </div>

      {/* Content */}
      <div className={prefix('__content')}>
        {/* Left Column */}
        <div className={prefix('__column', '__column--left')}>
          {/* Due Date Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Due Date</div>
            <div className={prefix('__select-wrapper')}>
              <select
                className={prefix('__select')}
                value={dueDateFilter || ''}
                onChange={e => onDueDateFilterChange((e.target.value || null) as DueDatePreset)}
              >
                <option value="">Any</option>
                {dueDateOptions.map(option => (
                  <option key={option.value} value={option.value || ''}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Date Range</div>
            <div className={prefix('__date-range')}>
              <div className={prefix('__date-input-wrapper')}>
                <label className={prefix('__date-label')}>From</label>
                <input
                  type="date"
                  className={prefix('__date-input')}
                  value={formatDateForInput(dateRangeFilter.from)}
                  onChange={handleFromDateChange}
                />
              </div>
              <div className={prefix('__date-input-wrapper')}>
                <label className={prefix('__date-label')}>To</label>
                <input
                  type="date"
                  className={prefix('__date-input')}
                  value={formatDateForInput(dateRangeFilter.to)}
                  onChange={handleToDateChange}
                  min={dateRangeFilter.from ? formatDateForInput(dateRangeFilter.from) : undefined}
                />
              </div>
            </div>
          </div>

          {/* Assignee Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Assignee</div>
            <div className={prefix('__select-wrapper')}>
              <select
                className={prefix('__select')}
                value={assigneeFilter || ''}
                onChange={e => onAssigneeFilterChange(e.target.value || null)}
              >
                <option value="">Any</option>
                {assigneeOptions.map(option => (
                  <option key={option.userId} value={option.userId}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className={prefix('__column', '__column--middle')}>
          {/* Status (WorkState) Filter */}
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Status</div>
            <div className={prefix('__options')}>
              {availableWorkStateOptions.map(option => (
                <label key={option.value} className={prefix('__option')}>
                  <input
                    type="checkbox"
                    checked={workStateFilter.has(option.value)}
                    onChange={() => toggleWorkState(option.value)}
                  />
                  <span className={prefix('__option-icon')}>{option.icon}</span>
                  <span className={prefix('__option-label')}>{option.label}</span>
                  <span className={prefix('__option-count')}>{workStateCounts[option.value]}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Request Type Filter */}
        <div className={prefix('__column', '__column--right')}>
          <div className={prefix('__section')}>
            <div className={prefix('__section-title')}>Request Type</div>
            <div className={prefix('__options')}>
              {requestTypeOptions.map(option => (
                <label key={option.value} className={prefix('__option')}>
                  <input
                    type="checkbox"
                    checked={requestTypeFilter.has(option.value)}
                    onChange={() => toggleRequestType(option.value)}
                  />
                  <span className={prefix('__option-label')}>{option.label}</span>
                  {requestTypeCounts && (
                    <span className={prefix('__option-count')}>{requestTypeCounts[option.value] || 0}</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={prefix('__footer')}>
        {hasActiveFilters && (
          <button className={prefix('__clear-all')} onClick={onClearAll}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};
